// **The flat HUD icons, by role** (`pvp-ui-fixes` W1).
//
// One registry so a callsite names a role rather than retyping a URL. The bug that motivated it
// was not a typo but a *convention*: every callsite wrote a bare `bee_worker.png`, which a browser
// resolves against the current URL's directory. That is identical to root-absolute on `/colony`
// and wrong on `/demo/red`, where it becomes `/demo/bee_worker.png`, gets the SPA fallback's
// `index.html` with a 200, and renders a broken image with nothing in the network log to show for
// it. A registry makes the correct URL the convenient one;
// `components/hud/icon-url-convention.test.ts` is what proves nobody bypassed it.
//
// **Root, not `/sprites/`.** These flat icons ship at `client/public/`'s top level, while
// `client/public/sprites/` mirrors `prototype/assets/sprites/` path-for-path. The two deliberately
// share bare filenames (`flower_dead.png` and friends), so flattening them would collide — see
// `sprite-assets.test.ts`'s note on the split.
//
// The bee-TYPE sprites are not here: they are keyed by a type's document name and belong with the
// colour and abbreviation that travel with them (`ui/rounds/type-visuals.ts`).

/** Every flat HUD icon shipped at the public root, by the role it depicts. */
export const HUD_ICONS = {
  /** The generic worker — a population count, not a bee type (`ResourceCounter`'s bees pill). */
  worker: '/bee_worker.png',
  /** The queen: one per colony, and not a player-definable type (DESIGN §3). */
  queen: '/bee_queen.png',
  /** A honey drop — stored nectar. */
  nectar: '/icon_nectar.png',
  /** One comb cell. */
  cell: '/icon_cell.png',
  /** Brood in a cell. */
  egg: '/icon_egg.png',
} as const;

export type HudIcon = (typeof HUD_ICONS)[keyof typeof HUD_ICONS];
